import type { Role } from '@/domain/env/schemas';
import type { CompanyCapabilities } from '@/domain/companies/schemas';
import type { ErpConnections } from '@/domain/erpIntegration/schemas';
import type { EndpointResponse } from '@/services/api/endpointTypes';
import type { envApi } from '@/domain/env/api';
import { companyConnectedErp } from '@/domain/erpIntegration/queries/connections.select';
import { roleModel } from '@/domain/env/role.select';
import {
  hasApprovals,
  hasFinancialDocuments,
  hasVendorBasedPolicyManagement,
  hasVendorPreferences,
  hasAdminOnlyApprovalFlowChanges,
} from '@/domain/companies/queries/capabilities.select';

/**
 * Every input of the case, whether a rule reads it or not: the settings tab bar shows every section on every settings
 * route, so no settings route opens before all of them are known.
 */
export type CompanySettingsAccessValues = {
  readonly role: Role | null;
  readonly isAdmin: boolean;
  readonly capabilities: CompanyCapabilities;
  readonly erpConnections: ErpConnections;
  readonly bankFeedUserPermissions: EndpointResponse<typeof envApi.getCompanyUserPreferences>;
};

export type CompanySettingsAccessContext = {
  readonly isAdmin: boolean;
  readonly isAccountant: boolean;
  readonly hasFinancialDocuments: boolean;
  readonly hasVendorPreferences: boolean;
  readonly hasApprovals: boolean;
  readonly hasVendorBasedPolicyManagement: boolean;
  readonly hasAdminOnlyApprovalFlowChanges: boolean;
  readonly bankFeedConnectionViewGranted: boolean;
  readonly connectedErp: string | null;
};

export const companySettingsAccessContext = ({
  role,
  isAdmin,
  capabilities,
  erpConnections,
  bankFeedUserPermissions,
}: CompanySettingsAccessValues): CompanySettingsAccessContext => ({
  isAdmin,
  isAccountant: roleModel.isAccountant({ role }),
  hasFinancialDocuments: hasFinancialDocuments(capabilities),
  hasVendorPreferences: hasVendorPreferences(capabilities),
  hasApprovals: hasApprovals(capabilities),
  hasVendorBasedPolicyManagement: hasVendorBasedPolicyManagement(capabilities),
  hasAdminOnlyApprovalFlowChanges: hasAdminOnlyApprovalFlowChanges(capabilities),
  bankFeedConnectionViewGranted: bankFeedUserPermissions.bank_feed_permissions.connection_view,
  connectedErp: companyConnectedErp(erpConnections),
});
